<template>
  <!-- SMP-1624 — the protocol of the stability, in the header: one pdf, the
       last upload replacing the previous one, in any status -->
  <div class="protocol-file-field">
    <el-button
      v-if="file"
      class="protocol-file-name"
      type="text"
      icon="fas fa-download"
      :loading="downloading"
      @click="download"
    >
      {{ file.filename }}
    </el-button>
    <span v-else-if="!canManage">{{ $t('stability.protocol.none') }}</span>

    <!-- Upload needs stabilityConfigManage; read-only users only download -->
    <el-upload
      v-if="canManage"
      ref="upload"
      action=""
      :http-request="upload"
      :before-upload="assertPdf"
      :show-file-list="false"
      :multiple="false"
      :limit="1"
      accept=".pdf"
      :disabled="uploading"
    >
      <el-button
        size="mini"
        icon="el-icon-upload2"
        :loading="uploading"
      >
        {{
          file
            ? $t('stability.protocol.replace')
            : $t('stability.protocol.upload')
        }}
      </el-button>
    </el-upload>
  </div>
</template>

<script>
  import fileSaver from 'file-saver';
  import { stabilityApi, filesApi } from '@/api';

  export default {
    name: 'ProtocolFileField',

    props: {
      configId: { type: Number, required: true },
      // The configuration's protocolFile, without its content; null if none
      file: { type: Object, default: null },
      canManage: { type: Boolean, default: false },
    },

    data() {
      return {
        uploading: false,
        downloading: false,
      };
    },

    methods: {
      // The API validates by extension too: checked here so the rejection
      // happens before the upload rather than as a 422. Returning false aborts
      // the upload without reaching http-request
      assertPdf(file) {
        if ((file.name || '').toLowerCase().endsWith('.pdf')) return true;
        this.$notify({
          type: 'error',
          showClose: true,
          message: this.$t('stability.protocol.wrongType'),
        });
        return false;
      },

      // Through the api client, not el-upload's own XHR: that one would not
      // carry the Authorization header the axios interceptor adds
      async upload({ file }) {
        this.uploading = true;
        try {
          await stabilityApi.uploadProtocol(this.configId, file);
          this.$emit('uploaded');
        } catch (err) {
          this.notifyError(err);
        } finally {
          this.uploading = false;
          if (this.$refs.upload) this.$refs.upload.clearFiles();
        }
      },

      async download() {
        this.downloading = true;
        try {
          const { data } = await filesApi.download(this.file.id);
          fileSaver.saveAs(data, this.file.filename);
        } catch (err) {
          this.notifyError(err);
        } finally {
          this.downloading = false;
        }
      },

      notifyError(err) {
        this.$notify({
          type: 'error',
          showClose: true,
          message:
            (err.response &&
              err.response.data &&
              err.response.data.error &&
              err.response.data.error.message) ||
            this.$t('stability.errors.generic'),
        });
      },
    },
  };
</script>

<style lang="scss">
  // Not scoped: the buttons are rendered by child components. The class names
  // are unique enough to stand on their own.
  .protocol-file-field {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }

  // el-button is white-space: nowrap, and file names carry no spaces to break
  // on: without this a long name overflows the descriptions cell
  .el-button.protocol-file-name {
    white-space: normal;
    word-break: break-all;
    text-align: left;
  }
</style>
